// ─── Section Renderer ─────────────────────────────────────────────────────────

import {
  TILE_SIZE,
  TILE_COLORS,
  TileType,
  type RoomData,
} from "./constants";
import type { Camera } from "./camera";
import type { SpriteSheet } from "./sprite-sheet";
import type { Character } from "./character";
import type { GeneratedAssets } from "./asset-generator";

export interface WorkstationInfo {
  index: number;
  label: string;
  status: "idle" | "working" | "done";
  toolIcon: string; // emoji or character for placeholder
}

/**
 * SectionRenderer draws section interiors — workstations with tool icons,
 * Surggies working, and task status indicators.
 */
export class SectionRenderer {
  /**
   * Draw the section view.
   *
   * @param ctx - Canvas rendering context
   * @param camera - Camera for offset
   * @param room - The section room data
   * @param surggies - Surggie characters assigned to this section
   * @param workstations - Workstation status information
   * @param sprites - SpriteSheet manager
   */
  draw(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    room: RoomData,
    surggies: Character[],
    workstations: WorkstationInfo[],
    sprites: SpriteSheet,
    assets?: GeneratedAssets,
  ): void {
    // Draw tile map
    this.drawTiles(ctx, room, assets);

    // Draw workstations
    this.drawWorkstations(ctx, room, workstations);

    // Draw Surggies at workstations
    for (const surggie of surggies) {
      surggie.draw(ctx, camera, sprites);
    }

    // Draw section label
    this.drawSectionLabel(ctx, room);
  }

  private drawTiles(ctx: CanvasRenderingContext2D, room: RoomData, assets?: GeneratedAssets): void {
    // Pick a floor asset if available (use first floor or fall back to null)
    const floorAsset = assets
      ? Object.values(assets.tiles.floors)[0] ?? null
      : null;
    const wallAsset = assets?.tiles.wall ?? null;

    for (let row = 0; row < room.height; row++) {
      for (let col = 0; col < room.width; col++) {
        const tile = room.tileMap[row][col] as TileType;
        const x = col * TILE_SIZE;
        const y = row * TILE_SIZE;

        // Use generated sprites for floor and wall tiles
        if (floorAsset && (tile === TileType.BuildingFloor || tile === TileType.Empty)) {
          ctx.drawImage(floorAsset as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
          continue;
        }
        if (wallAsset && tile === TileType.Wall) {
          ctx.drawImage(wallAsset as CanvasImageSource, x, y, TILE_SIZE, TILE_SIZE);
          continue;
        }

        // Fallback: flat color
        const color = TILE_COLORS[tile] || TILE_COLORS[TileType.Empty];
        ctx.fillStyle = color;
        ctx.fillRect(x, y, TILE_SIZE, TILE_SIZE);
      }
    }
  }

  private drawWorkstations(
    ctx: CanvasRenderingContext2D,
    room: RoomData,
    workstations: WorkstationInfo[],
  ): void {
    for (const ws of workstations) {
      const interactable = room.interactables.find(
        (i) => i.data?.workstationIndex === ws.index,
      );
      if (!interactable) continue;

      const x = interactable.position.x;
      const y = interactable.position.y;
      const w = interactable.width;
      const h = interactable.height;

      // Desk surface
      ctx.fillStyle = "#7B5B3A";
      ctx.fillRect(x, y, w, h);

      // Desk border
      ctx.strokeStyle = "#4A3520";
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, w, h);

      // Tool icon (placeholder text)
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "bold 12px monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(ws.toolIcon, x + w / 2, y + h / 2 - 4);

      // Status indicator
      let statusColor: string;
      switch (ws.status) {
        case "working":
          statusColor = "#F39C12";
          break;
        case "done":
          statusColor = "#27AE60";
          break;
        default:
          statusColor = "#95A5A6";
      }

      // Small status dot
      ctx.fillStyle = statusColor;
      ctx.beginPath();
      ctx.arc(x + w - 6, y + 6, 4, 0, Math.PI * 2);
      ctx.fill();

      // Label below desk
      ctx.fillStyle = "#ECF0F1";
      ctx.font = "8px monospace";
      ctx.textAlign = "center";
      ctx.fillText(ws.label, x + w / 2, y + h + 10);
    }
  }

  private drawSectionLabel(
    ctx: CanvasRenderingContext2D,
    room: RoomData,
  ): void {
    const centerX = (room.width * TILE_SIZE) / 2;

    ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
    ctx.font = "bold 12px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    const text = room.name;
    const metrics = ctx.measureText(text);
    const tw = metrics.width + 16;

    ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
    ctx.beginPath();
    ctx.roundRect(centerX - tw / 2, 4, tw, 18, 4);
    ctx.fill();

    ctx.fillStyle = "#F4D03F";
    ctx.fillText(text, centerX, 7);
  }
}
